import React from 'react';
import { Link } from 'react-router-dom';
import Accordion, { type AccordionItem } from '../components/Accordion';
import { Button } from '../components/ui/Button';
import { CONTACT_EMAIL, HOSTING_URL, QUOTE_URL, mailto } from '../lib/site';

const textLink =
  'text-primary-300 hover:text-primary-200 underline underline-offset-4 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400';

const BUILDS = [
  { t: 'Websites', d: 'Single page to multi-page, with custom design, navigation and motion.' },
  { t: 'Web applications', d: 'Backend logic, APIs and integrations.' },
  { t: 'Database-backed systems', d: 'Accounts, records and authentication.' },
  { t: 'High-reliability platforms', d: 'Redundancy and sensitive-data handling, scoped individually.' },
];

const DEVICES = [
  'Kiosks for customer interfacing',
  'Always-on slideshows',
  'Digital menus that can be updated quickly',
];

const START = [
  {
    t: 'Say what you need',
    d: 'A website, a web application, a database-backed system, a high-reliability platform, or a custom device deployment.',
  },
  {
    t: 'Get a development quote',
    d: 'A development quote is for website and application builds. It is produced on the specialized site, and the button opens that page directly. It does not cover custom device deployment. The Hub does not price the work itself.',
  },
  {
    t: 'Look at what we already build',
    d: 'Our own products, live and in development, are written up on Our Work. Commissioned services and those products are separate.',
  },
];

const OTHER_AREAS: AccordionItem[] = [
  { id: 'tech', title: 'Technology solutions', teaser: 'Custom software, web applications, and digital transformation.', content: 'Custom software development, web applications and digital transformation tailored to your business. A quote for that software work comes from the specialized site. Custom device deployment is a separate inquiry.' },
  { id: 'growth', title: 'Business growth', teaser: 'Consulting and scalable operations.', content: 'Strategic consulting and scalable solutions to help businesses expand and optimize operations. It sits beside custom development, not in place of a quote for a build.' },
  { id: 'games', title: 'Game development', teaser: 'Storytelling and gameplay.', content: 'Immersive gaming experiences with engaging storytelling and innovative gameplay.' },
  { id: 'edu', title: 'Innovation & education', teaser: 'Research plus learning programs.', content: 'Research and development of new technologies combined with educational programs. The same idea shows up in our value of education: continuous learning and knowledge sharing.' },
  { id: 'access', title: 'Accessible technology', teaser: 'Advanced tools, kept affordable.', content: 'Making advanced technology accessible and affordable through user-friendly, cost-effective solutions. That matches the published aim of products and solutions at low expense to the consumer.' },
  { id: 'opportunity', title: 'Opportunities for all', teaser: 'Pathways into technology.', content: 'Inclusive pathways for people from all backgrounds into technology, education and careers. No positions are currently available; the careers page says so plainly.' },
];

const Services: React.FC = () => (
  <div style={{ background: 'radial-gradient(ellipse 80% 40% at 50% 0%, rgba(14,165,233,0.06) 0%, transparent 60%)' }}>
    <section className="page-shell page-section-major">
      <div className="page-inner">
        <header className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-2">
            <span className="cw">Our </span>
            <span className="cb">Services</span>
          </h1>
          <p className="text-secondary-400">
            FairCorp takes on commissioned websites, applications, and custom device deployment, and it builds its own products. This page is the commissioned work. The products are on{' '}
            <Link to="/work" className={textLink}>Our Work</Link>.
          </p>
        </header>

        <section aria-labelledby="svc-dev" className="rounded-2xl border border-secondary-700/60 bg-secondary-900 p-5 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div>
              <h2 id="svc-dev" className="text-xl sm:text-2xl font-bold text-white">Custom development</h2>
              <p className="text-secondary-300 mt-1">Websites and applications designed and built to your requirements.</p>
            </div>
            <Button href={QUOTE_URL} className="shrink-0">Get a quote ↗</Button>
          </div>
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 border-t border-secondary-800 pt-4">
            {BUILDS.map((b) => (
              <div key={b.t}>
                <dt className="font-semibold text-primary-200 text-sm">{b.t}</dt>
                <dd className="text-secondary-400 text-sm">{b.d}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section aria-labelledby="svc-devices" className="rounded-2xl border border-secondary-700/60 bg-secondary-900 p-5 sm:p-6 mb-6">
          <h2 id="svc-devices" className="text-xl sm:text-2xl font-bold text-white">Custom device deployment</h2>
          <p className="text-secondary-300 mt-1">
            FairCorp configures devices customers interact with, including:
          </p>
          <ul className="list-disc pl-5 mt-3 space-y-1 text-sm text-secondary-300">
            {DEVICES.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>And more</li>
          </ul>
          <p className="text-secondary-400 text-sm mt-2">
            Registers are a possible later expansion, not a current offer.
          </p>
          <p className="text-sm text-secondary-300 mt-4">
            Ask about a device deployment by email. This inquiry is separate from the website and application quote.{' '}
            <a href={mailto('Custom device deployment')} className={`font-semibold break-all ${textLink}`}>{CONTACT_EMAIL}</a>
          </p>
        </section>

        <section aria-labelledby="svc-start" className="mb-6">
          <h2 id="svc-start" className="text-lg font-bold text-white mb-3">How an engagement starts</h2>
          <ol className="rounded-2xl border border-secondary-700/60 bg-secondary-900 divide-y divide-secondary-800/70">
            {START.map((step, i) => (
              <li key={step.t} className="p-4 sm:p-5">
                <h3 className="font-semibold text-white">
                  <span className="text-primary-300 mr-2">{i + 1}.</span>
                  {step.t}
                </h3>
                <p className="text-sm text-secondary-400 mt-1">{step.d}</p>
              </li>
            ))}
          </ol>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4">
            <Button href={QUOTE_URL}>Get a quote ↗</Button>
            <Link to="/work" className={`inline-flex items-center min-h-[44px] text-sm font-semibold ${textLink}`}>Our Work →</Link>
          </div>
          <p className="text-sm text-secondary-400 mt-3">
            Questions before a quote:{' '}
            <a href={mailto('Question about a FairCorp build')} className={`font-semibold break-all ${textLink}`}>{CONTACT_EMAIL}</a>
          </p>
        </section>

        <section aria-labelledby="svc-other" className="mb-6">
          <h2 id="svc-other" className="text-lg font-bold text-white mb-3">Other areas we work in</h2>
          <Accordion items={OTHER_AREAS} multiple />
        </section>

        <p className="text-sm text-secondary-400 leading-relaxed">
          Our own products are on <Link to="/work" className={textLink}>Our Work</Link>.
          {' '}Need web hosting? It is handled on our specialized site:{' '}
          <a href={HOSTING_URL} className={textLink}>FairCorp Hosting ↗</a>
        </p>
      </div>
    </section>
  </div>
);

export default Services;
